import dayjs from "dayjs";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { weekdayKu } from "../../utils/surgeryAppointments";
import "./styles.css";

const ITEM_H = 34;
const VIEW_COUNT = 5;
const PAD_H = ITEM_H * Math.floor(VIEW_COUNT / 2);

function pad2(n) {
  return String(n).padStart(2, "0");
}

const HOURS = Array.from({ length: 12 }, (_, i) => {
  const hour = i + 1;
  return { value: hour, label: String(hour) };
});

const MINUTES = Array.from({ length: 60 }, (_, i) => ({ value: i, label: pad2(i) }));

const PERIODS = [
  { value: "AM", label: "AM" },
  { value: "PM", label: "PM" },
];

function clockParts(value) {
  const parsed = value ? dayjs(String(value).slice(0, 5), "HH:mm") : dayjs();
  const time = parsed.isValid() ? parsed : dayjs();
  const hour24 = time.hour();
  return {
    hour: hour24 % 12 === 0 ? 12 : hour24 % 12,
    minute: time.minute(),
    period: hour24 >= 12 ? "PM" : "AM",
  };
}

function toClock(hour, minute, period) {
  const hour24 = period === "AM" ? (hour === 12 ? 0 : hour) : hour === 12 ? 12 : hour + 12;
  return `${pad2(hour24)}:${pad2(minute)}`;
}

function daysInMonth(year, month) {
  return dayjs(`${year}-${pad2(month)}-01`).daysInMonth();
}

function WheelColumn({ items, value, onChange, markOf, idPrefix }) {
  const scroller = useRef(null);
  const ignore = useRef(false);
  const settle = useRef(0);
  const index = Math.max(0, items.findIndex((item) => item.value === value));
  const [active, setActive] = useState(index);

  useEffect(() => {
    setActive(index);
    const el = scroller.current;
    if (!el) return;
    ignore.current = true;
    el.scrollTop = index * ITEM_H;
    const timer = window.setTimeout(() => {
      ignore.current = false;
    }, 80);
    return () => window.clearTimeout(timer);
  }, [index, items.length]);

  const pick = (nextIndex, smooth) => {
    const item = items[nextIndex];
    if (!item) return;
    const el = scroller.current;
    if (el) {
      ignore.current = true;
      el.scrollTo({ top: nextIndex * ITEM_H, behavior: smooth ? "smooth" : "auto" });
      window.setTimeout(() => {
        ignore.current = false;
      }, 140);
    }
    setActive(nextIndex);
    if (item.value !== value) onChange(item.value);
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const next = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ITEM_H)));
    setActive(next);
    if (ignore.current) return;
    window.clearTimeout(settle.current);
    settle.current = window.setTimeout(() => pick(next, true), 120);
  };

  return (
    <div
      className="apt-wheel-col"
      ref={scroller}
      onScroll={onScroll}
      role="listbox"
      aria-activedescendant={items[active] ? `${idPrefix}-${items[active].value}` : undefined}
    >
      <div className="apt-wheel-pad" style={{ height: PAD_H }} />
      {items.map((item, i) => {
        const mark = markOf?.(item.value) || "";
        const on = i === active;
        return (
          <button
            key={item.value}
            id={`${idPrefix}-${item.value}`}
            type="button"
            role="option"
            aria-selected={on}
            className={`apt-wheel-item${on ? " is-on" : ""}${mark ? ` is-${mark}` : ""}`}
            onClick={() => pick(i, true)}
          >
            {item.label}
          </button>
        );
      })}
      <div className="apt-wheel-pad" style={{ height: PAD_H }} />
    </div>
  );
}

export function AptDatePicker({ value, onChange, markDay, placeholder }) {
  const uid = useId().replace(/:/g, "");
  const selected = value && dayjs(value).isValid() ? dayjs(value) : dayjs();
  const year = selected.year();
  const month = selected.month() + 1;
  const day = selected.date();
  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: pad2(i + 1) })),
    []
  );
  const dayCount = daysInMonth(year, month);
  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => ({ value: i + 1, label: pad2(i + 1) })),
    [dayCount]
  );

  const emit = (nextYear, nextMonth, nextDay) => {
    const last = daysInMonth(nextYear, nextMonth);
    const safeDay = Math.min(nextDay, last);
    const next = dayjs(`${nextYear}-${pad2(nextMonth)}-${pad2(safeDay)}`);
    onChange?.(next, next.format("YYYY-MM-DD"));
  };

  const mark = selected && markDay ? markDay(selected) : "";

  return (
    <div className="apt-wheel-wrap" aria-label={placeholder}>
      <div className="apt-wheel-labels">
        <span>مانگ</span>
        <span>ڕۆژ</span>
      </div>
      <div className="apt-wheel" style={{ height: ITEM_H * VIEW_COUNT }}>
        <div className="apt-wheel-band" />
        <WheelColumn
          idPrefix={`${uid}-month`}
          items={months}
          value={month}
          onChange={(next) => emit(year, next, day)}
        />
        <WheelColumn
          idPrefix={`${uid}-day`}
          items={days}
          value={Math.min(day, dayCount)}
          onChange={(next) => emit(year, month, next)}
          markOf={(d) => markDay?.(dayjs(`${year}-${pad2(month)}-${pad2(d)}`)) || ""}
        />
      </div>
      <div className={`apt-wheel-caption${mark ? ` is-${mark}` : ""}`}>
        {weekdayKu(selected)} · {selected.format("YYYY/MM/DD")}
      </div>
    </div>
  );
}

export function AptTimePicker({ value, onChange, placeholder = "کات هەڵبژێرە" }) {
  const uid = useId().replace(/:/g, "");
  const { hour, minute, period } = clockParts(value);
  const clock = toClock(hour, minute, period);
  const label = dayjs(clock, "HH:mm").format("h:mm A");

  const emit = (nextHour, nextMinute, nextPeriod) => {
    onChange?.(toClock(nextHour, nextMinute, nextPeriod));
  };

  return (
    <div className="apt-wheel-wrap" aria-label={placeholder}>
      <div className="apt-wheel-labels">
        <span>کاتژمێر</span>
        <span>خولەک</span>
        <span />
      </div>
      <div className="apt-wheel" style={{ height: ITEM_H * VIEW_COUNT }}>
        <div className="apt-wheel-band" />
        <WheelColumn
          idPrefix={`${uid}-hour`}
          items={HOURS}
          value={hour}
          onChange={(next) => emit(next, minute, period)}
        />
        <WheelColumn
          idPrefix={`${uid}-minute`}
          items={MINUTES}
          value={minute}
          onChange={(next) => emit(hour, next, period)}
        />
        <WheelColumn
          idPrefix={`${uid}-period`}
          items={PERIODS}
          value={period}
          onChange={(next) => emit(hour, minute, next)}
        />
      </div>
      <div className="apt-wheel-caption">{label}</div>
    </div>
  );
}
